import React, { useEffect, useState, useCallback } from 'react'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { EnrollmentApi } from '@/features/enrollment'
import { GamerTagService } from '@/features/enrollment'
import { validateAgeStartedGaming, calculateCurrentAge } from '@/shared/utils'
import { handleValidationErrors } from '@/shared/utils'
import { validateStep2 } from '../validation/intakeContactValidation'
import IntakeForm from './IntakeForm'
import ContactMethod from './ContactMethod'
import ReportProblemButton from './ReportProblemButton'
import { Step2IntakeContactProps, ValidationErrors, GamerTagResult } from '@/features/enrollment'

export default function Step2IntakeContact({
  enrollmentData,
  updateEnrollmentData,
  generateQRCode,
  sendEmailLink,
  handleNavigate,
  handleSimpleNavigate,
  sessionId,
  isNavigating,
  isCompleting,
  campaign,
}: Step2IntakeContactProps) {
  // State for legal guardian confirmation
  const [legalGuardianConfirmed, setLegalGuardianConfirmed] = useState(false)
  const [reportedProblems, setReportedProblems] = useState<Record<string, boolean>>({})
  
  // State for opt-in form submission status
  const [optInStatus, setOptInStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [optInMessage, setOptInMessage] = useState('')


  // Use ref to track previous relationToVictim to only run effect when it changes
  const prevRelationRef = React.useRef(enrollmentData.relationToVictim)

  // Auto-populate caller fields and retainer agreement name based on relation to victim
  // Only run when relationToVictim changes, NOT when names change
  React.useEffect(() => {
    // Only run when relationToVictim actually changes, not when names change
    if (prevRelationRef.current !== enrollmentData.relationToVictim) {
      prevRelationRef.current = enrollmentData.relationToVictim
      
      if (enrollmentData.relationToVictim && enrollmentData.intakeFirstName && enrollmentData.intakeLastName) {
        if (enrollmentData.relationToVictim === 'Myself') {
          // "Myself" - populate injured party and retainer agreement with gamer's name
          updateEnrollmentData('injuredPartyFirstName', enrollmentData.intakeFirstName)
          updateEnrollmentData('injuredPartyLastName', enrollmentData.intakeLastName)
          updateEnrollmentData('retainerAgreementFullName', `${enrollmentData.intakeFirstName} ${enrollmentData.intakeLastName}`)
        } else if (enrollmentData.relationToVictim === 'Loved one') {
          // "Loved One" - do NOT auto-populate caller fields (Video Gamer fields should be blank)
          // User must manually enter the video gamer's name separately
          // Only populate retainer agreement with Legal Guardian's name
          updateEnrollmentData('retainerAgreementFullName', `${enrollmentData.intakeFirstName} ${enrollmentData.intakeLastName}`)
          // Clear any existing values in caller fields to ensure they're blank
          if (enrollmentData.callerFirstName) {
            updateEnrollmentData('callerFirstName', '')
          }
          if (enrollmentData.callerLastName) {
            updateEnrollmentData('callerLastName', '')
          }
        }
      }
    }
    // ✅ CRITICAL: Only depend on relationToVictim, NOT on firstName/lastName
    // This prevents the effect from running on every keystroke
  }, [enrollmentData.relationToVictim, updateEnrollmentData])

  // Separate effect to ensure Video Gamer fields are never synced from Legal Guardian fields
  // This runs whenever intakeFirstName/intakeLastName change to prevent accidental syncing
  React.useEffect(() => {
    if (enrollmentData.relationToVictim === 'Loved one') {
      // If callerFirstName/callerLastName somehow got set to intakeFirstName/intakeLastName, clear them
      // This prevents accidental syncing from Legal Guardian fields
      if (enrollmentData.callerFirstName === enrollmentData.intakeFirstName && enrollmentData.intakeFirstName) {
        updateEnrollmentData('callerFirstName', '')
      }
      if (enrollmentData.callerLastName === enrollmentData.intakeLastName && enrollmentData.intakeLastName) {
        updateEnrollmentData('callerLastName', '')
      }
    }
  }, [enrollmentData.relationToVictim, enrollmentData.intakeFirstName, enrollmentData.intakeLastName, enrollmentData.callerFirstName, enrollmentData.callerLastName, updateEnrollmentData])


  // Auto-populate 1200+ hours question based on Step 1 verification data
  React.useEffect(() => {
    if (enrollmentData.platforms && !enrollmentData.hoursOver1200) {
      let totalHours = 0
      
      // Calculate total hours from all verified platforms
      Object.values(enrollmentData.platforms).forEach((platform: any) => {
        if (platform?.verificationResult?.totalHoursPlayed) {
          totalHours += platform.verificationResult.totalHoursPlayed
        }
      })
      
      // Auto-complete as "Yes" if total hours is 1100 or more
      if (totalHours >= 1100) {
        updateEnrollmentData('hoursOver1200', 'one') // 'one' corresponds to "Yes"
      }
    }
  }, [enrollmentData.platforms, enrollmentData.hoursOver1200, updateEnrollmentData])

  // Auto-populate platforms from Step1 selection on component mount
  React.useEffect(() => {
    if (enrollmentData.selectedPlatforms && enrollmentData.selectedPlatforms.length > 0) {
      // Map Step1 platform names to Step2 platform names
      const platformMapping: { [key: string]: string } = {
        'xbox': 'Xbox',
        'playstation': 'PlayStation', 
        'steam': 'Steam'
      }
      
      const mappedPlatforms = enrollmentData.selectedPlatforms
        .map((platform: string) => platformMapping[platform])
        .filter((platform: string) => platform) // Remove any undefined mappings
      
      // Only update if intakePlatforms is empty or different from selected platforms
      const currentIntakePlatforms = enrollmentData.intakePlatforms || []
      const shouldUpdate = currentIntakePlatforms.length === 0 || 
        !mappedPlatforms.every((platform: string) => currentIntakePlatforms.includes(platform))
      
      if (shouldUpdate && mappedPlatforms.length > 0) {
        updateEnrollmentData('intakePlatforms', mappedPlatforms)
        
        // Auto-populate verified gamer tags from Step1
        enrollmentData.selectedPlatforms.forEach((platform: string) => {
          const platformData = enrollmentData.platforms?.[platform]
          if (platformData && platformData.gamerTag && platformData.verificationResult && platformData.verificationResult.pass) {
            // Map platform names to intake field names
            const fieldMapping: { [key: string]: string } = {
              'xbox': 'intakeXboxGamerTag',
              'playstation': 'intakePlaystationGamerTag',
              'steam': 'intakeSteamGamerTag'
            }
            
            const fieldName = fieldMapping[platform]
            if (fieldName && !enrollmentData[fieldName]) {
              updateEnrollmentData(fieldName, platformData.gamerTag)
            }
          }
        })
      }
    }
  }, [enrollmentData.selectedPlatforms, enrollmentData.platforms, enrollmentData.intakePlatforms, updateEnrollmentData])
  
  const [errors, setErrors] = useState<ValidationErrors>({})

  // Real-time validation for hours per day
  const validateHoursPerDay = (value: string) => {
    const newErrors = { ...errors }
    
    if (!value) {
      newErrors.hoursPerDay = 'Hours per day is required'
    } else {
      const hours = parseFloat(value)
      if (isNaN(hours) || hours < 0.1) {
        newErrors.hoursPerDay = 'Please enter a valid number of hours'
      } else if (hours < 3) {
        newErrors.hoursPerDay = 'Must be at least 3 hours per day'
      } else if (hours > 24) {
        newErrors.hoursPerDay = 'Cannot exceed 24 hours per day'
      } else {
        delete newErrors.hoursPerDay
      }
    }
    
    setErrors(newErrors)
  }

  // Real-time validation for age when started gaming
  const validateAgeStartedGamingRealTime = (value: string) => {
    const newErrors = { ...errors }
    
    if (!value) {
      newErrors.ageStartedGaming = 'Age when started gaming is required'
    } else {
      const validationResult = validateAgeStartedGaming(
        value,
        enrollmentData.birthYear,
        enrollmentData.birthMonth,
        enrollmentData.birthDay
      )
      
      if (!validationResult.isValid) {
        newErrors.ageStartedGaming = validationResult.errorMessage || 'Invalid age'
      } else {
        delete newErrors.ageStartedGaming
      }
    }
    
    setErrors(newErrors)
  }

  // Auto-populate age when birth date is first completed (but don't interfere with user edits)
  React.useEffect(() => {
    if (enrollmentData.birthYear && enrollmentData.birthMonth && enrollmentData.birthDay && !enrollmentData.ageStartedGaming) {
      // Calculate current age
      const currentAge = calculateCurrentAge(enrollmentData.birthYear, enrollmentData.birthMonth, enrollmentData.birthDay)
      
      if (currentAge) {
        // Auto-populate with a reasonable starting age (minimum 5, but not more than current age - 2)
        const suggestedAge = Math.max(5, Math.min(currentAge - 2, 12))
        updateEnrollmentData('ageStartedGaming', suggestedAge.toString())
      }
    }
    
  }, [enrollmentData.birthYear, enrollmentData.birthMonth, enrollmentData.birthDay])

  const [gamerTagResults, setGamerTagResults] = useState<Record<string, GamerTagResult | null>>({})
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})

  const handleCheckGamerTag = async (platform: string, gamerTag: string) => {
    if (!gamerTag) {
      alert('Please enter a gamer tag first')
      return
    }

    setLoadingStates(prev => ({ ...prev, [platform]: true }))

    try {
      const result = await GamerTagService.checkGamerTag(gamerTag, platform)
      
      // Update the gamer tag results state
      setGamerTagResults(prev => ({ ...prev, [platform]: result }))
      
      if (result.pass) {
        // Auto-populate the corresponding intake field with the verified gamer tag
        const platformKey = platform.toLowerCase()
        const intakeFieldMapping: { [key: string]: string } = {
          'xbox': 'intakeXboxGamerTag',
          'playstation': 'intakePlaystationGamerTag',
          'steam': 'intakeSteamGamerTag'
        }
        
        const intakeField = intakeFieldMapping[platformKey]
        if (intakeField) {
          updateEnrollmentData(intakeField, result.gamer_tag)
        }
        
        // Also update Step1 data in the database to reflect the new platform verification
        // This ensures that the platforms data is consistent across both steps
        // BUT only if gamer tags are not skipped
        if (sessionId && !enrollmentData.gamerTagsSkipped) {
          try {
            const currentSelectedPlatforms = enrollmentData.selectedPlatforms || []
            const updatedSelectedPlatforms = currentSelectedPlatforms.includes(platformKey) 
              ? currentSelectedPlatforms 
              : [...currentSelectedPlatforms, platformKey]
              
            const step1Payload = {
              selectedPlatform: enrollmentData.selectedPlatform,
              selectedPlatforms: updatedSelectedPlatforms,
              platforms: enrollmentData.platforms,
            }
            
            // Actually save the Step1 data to the database
            EnrollmentApi.saveStepAuto({
              sessionId,
              stepId: 'step1',
              stepIndex: 0,
              stepData: step1Payload,
            }).then(() => {
              console.log('Successfully updated Step1 data in database with new platform verification')
            }).catch((error) => {
              console.error('Failed to update Step1 data with new platform verification:', error)
            })
            
          } catch (error) {
            console.error('Error preparing Step1 data update:', error)
          }
        }
      }
    } catch (error: any) {
      console.error('Error checking gamer tag:', error)
      
      // Handle API errors similar to Step1
      if (error.response && error.response.data) {
        const apiError = error.response.data
        const errorResult = {
          gamer_tag: gamerTag,
          totalHoursPlayed: 0,
          pass: false,
          error: apiError.error || 'API Error',
          details: apiError.details || 'Gamer Tag not found or is private'
        }
        setGamerTagResults(prev => ({ ...prev, [platform]: errorResult }))
      } else {
        const errorResult = {
          gamer_tag: gamerTag,
          totalHoursPlayed: 0,
          pass: false,
          error: 'Network Error',
          details: 'Failed to connect to verification service. Please try again.'
        }
        setGamerTagResults(prev => ({ ...prev, [platform]: errorResult }))
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, [platform]: false }))
    }
  }

  const handleReportProblem = async (platform: string) => {
    try {
      
      // Mark as reported
      setReportedProblems(prev => ({ ...prev, [platform]: true }))
      
      alert(`Problem reported for ${platform} gamer tag verification. You can continue with the enrollment process.`)
    } catch (error) {
      console.error('Error reporting problem:', error)
      alert('Failed to report problem. Please try again.')
    }
  }

  const renderGamerTagResults = (platform: string) => {
    const result = gamerTagResults[platform]
    if (!result) return null

    const isError = 'error' in result && result.error !== undefined
    const bgColor = isError ? 'bg-red-50' : (result.pass ? 'bg-green-50' : 'bg-red-50')
    const borderColor = isError ? 'border-red-200' : (result.pass ? 'border-green-200' : 'border-red-200')

    return (
      <div className={`mt-2 p-2 ${bgColor} rounded border ${borderColor} text-xs`}>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-600">Gamer Tag:</span>
            <span className="font-medium">{result.gamer_tag}</span>
          </div>
          {isError ? (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Error:</span>
                <span className="font-medium">{'error' in result ? result.error : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Details:</span>
                <span className="font-medium">{'details' in result ? result.details : ''}</span>
              </div>
              <div className="mt-3 pt-2 border-t border-red-200">
                <ReportProblemButton
                  onReportProblem={() => handleReportProblem(platform)}
                  isReported={reportedProblems[platform] || false}
                  className="w-full justify-center"
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Hours Played:</span>
                <span className="font-medium">{result.totalHoursPlayed.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${result.pass ? 'text-green-600' : 'text-red-600'}`}>
                  {result.pass ? 'PASS' : 'FAIL'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // Handle opt-in form submission to lawsuitsform/submit API
  const handleOptInSubmission = async () => {
    if (!sessionId) {
      setOptInStatus('error')
      setOptInMessage('Session ID is required for opt-in submission')
      return
    }

    setOptInStatus('submitting')
    setOptInMessage('Submitting to opt-in form...')
    
    try {
      const response = await EnrollmentApi.submitOptInForm(sessionId)
      
      if (response.success) {
        setOptInStatus('success')
        setOptInMessage(response.data.message)
        updateEnrollmentData('optInFormSubmitted', true)
        updateEnrollmentData('optInSubmissionStatus', response.data.status)
        updateEnrollmentData('optInSubmittedAt', response.data.submittedAt)
      } else {
        throw new Error(response.error || 'Submission failed')
      }
    } catch (error: any) {
      setOptInStatus('error')
      setOptInMessage(`Failed to submit to opt-in form: ${error.message}`)
      updateEnrollmentData('optInFormSubmitted', false)
      updateEnrollmentData('optInSubmissionStatus', 'failed')
    }
  }

  const handleContinue = () => {
    // Check if this is a Roblox campaign
    const isRobloxCampaign = campaign?.name?.toLowerCase().includes('roblox') || 
                            campaign?.description?.toLowerCase().includes('roblox') ||
                            enrollmentData.isMinor !== undefined
    
    let validationErrors = validateStep2(enrollmentData, legalGuardianConfirmed)
    
    // For Roblox campaigns, be more lenient with validation
    if (isRobloxCampaign) {
      // Remove validation errors for gaming-specific fields that aren't relevant for Roblox
      const robloxIrrelevantFields = [
        'ageStartedGaming', 'hoursPerDay', 'intakePlatforms', 'intakeGames', 
        'hoursOver1200', 'intakeXboxGamerTag', 'intakePlaystationGamerTag', 'intakeSteamGamerTag'
      ]
      
      robloxIrrelevantFields.forEach(field => {
        delete validationErrors[field]
      })
      
    }
    
    setErrors(validationErrors)
    
    if (Object.keys(validationErrors).length === 0) {
      handleNavigate(2)
    } else {
      // Scroll to first error field and highlight all errors
      handleValidationErrors(validationErrors)
    }
  }

  const handleBack = () => {
    setErrors({})
    if (handleSimpleNavigate) {
      handleSimpleNavigate(0)
    } else {
      handleNavigate(0)
    }
  }
  
  useEffect(() => {
    if (!sessionId) return
    if (enrollmentData.emailLinkVerified) return
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
    const url = `${apiBase}/enrollments/public/sessions/${sessionId}/verify/stream`
    const es = new EventSource(url)
    es.addEventListener('verified', (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data)
        if (data?.emailLinkVerified) {
          updateEnrollmentData('emailLinkVerified', true)
          if (data.idDocumentUrl !== undefined) updateEnrollmentData('idDocumentUrl', data.idDocumentUrl)
          if (data.consentAgreed !== undefined) updateEnrollmentData('identityConsentAgreed', data.consentAgreed)
        }
      } catch {}
    })
    es.addEventListener('error', () => {
      // Silent
    })
    // Polling fallback in case SSE is blocked
    const poll = setInterval(async () => {
      try {
        const session = await EnrollmentApi.getSession(sessionId)
        if ((session as any).emailLinkVerified) {
          updateEnrollmentData('emailLinkVerified', true)
          if ((session as any).idDocumentUrl !== undefined) updateEnrollmentData('idDocumentUrl', (session as any).idDocumentUrl)
          if ((session as any).consentAgreed !== undefined) updateEnrollmentData('identityConsentAgreed', (session as any).consentAgreed)
        }
      } catch {}
    }, 5000)

    return () => {
      es.close()
      clearInterval(poll)
    }
  }, [sessionId])

  // Function to get pre-checked platforms from Step1 and auto-populate gamer tags
  const getPreCheckedPlatforms = () => {
    // Check both selectedPlatform (string) and selectedPlatforms (array) for compatibility
    const selectedPlatform = enrollmentData.selectedPlatform || ''
    const selectedPlatforms = enrollmentData.selectedPlatforms || []
    
    // Convert single platform to array if needed
    const platformsArray = selectedPlatforms.length > 0 ? selectedPlatforms : (selectedPlatform ? [selectedPlatform] : [])
    
    const mappedPlatforms = platformsArray.map((platform: string) => {
      switch (platform) {
        case 'playstation': return 'PlayStation'
        case 'xbox': return 'Xbox'
        case 'steam': return 'Steam'
        default: return platform
      }
    })
    
    // Auto-populate platforms if not already set
    if (platformsArray.length > 0 && (!enrollmentData.intakePlatforms || enrollmentData.intakePlatforms.length === 0)) {
      updateEnrollmentData('intakePlatforms', mappedPlatforms)
    }
    
    return mappedPlatforms
  }

  // Auto-populate gamer tags from Step1 data
  useEffect(() => {
    // Check both selectedPlatform (string) and selectedPlatforms (array) for compatibility
    const selectedPlatform = enrollmentData.selectedPlatform || ''
    const selectedPlatforms = enrollmentData.selectedPlatforms || []
    const platformsArray = selectedPlatforms.length > 0 ? selectedPlatforms : (selectedPlatform ? [selectedPlatform] : [])
    const platforms = enrollmentData.platforms || {}
    
    
    // Auto-populate PlayStation gamer tag - check multiple possible platform names
    const hasPlayStation = platformsArray.includes('playstation') || platformsArray.includes('PlayStation') || platformsArray.includes('psn')
    if (hasPlayStation && platforms.playstation?.gamerTag && !enrollmentData.intakePlaystationGamerTag) {
      updateEnrollmentData('intakePlaystationGamerTag', platforms.playstation.gamerTag)
    }
    
    // Auto-populate Xbox gamer tag - check multiple possible platform names
    const hasXbox = platformsArray.includes('xbox') || platformsArray.includes('Xbox') || platformsArray.includes('XBOX')
    if (hasXbox && platforms.xbox?.gamerTag && !enrollmentData.intakeXboxGamerTag) {
      updateEnrollmentData('intakeXboxGamerTag', platforms.xbox.gamerTag)
    }
    
    // Auto-populate Steam gamer tag - check multiple possible platform names
    const hasSteam = platformsArray.includes('steam') || platformsArray.includes('Steam') || platformsArray.includes('STEAM')
    if (hasSteam && platforms.steam?.gamerTag && !enrollmentData.intakeSteamGamerTag) {
      updateEnrollmentData('intakeSteamGamerTag', platforms.steam.gamerTag)
    }
  }, [enrollmentData.selectedPlatform, enrollmentData.selectedPlatforms, enrollmentData.platforms])

  // Initialize platforms from Step1 data
  useEffect(() => {
    getPreCheckedPlatforms()
  }, [])

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Intake Form Section */}
      <IntakeForm
        enrollmentData={enrollmentData}
        updateEnrollmentData={updateEnrollmentData}
        errors={errors}
        legalGuardianConfirmed={legalGuardianConfirmed}
        setLegalGuardianConfirmed={setLegalGuardianConfirmed}
        handleCheckGamerTag={handleCheckGamerTag}
        loadingStates={loadingStates}
        renderGamerTagResults={renderGamerTagResults}
        validateAgeStartedGamingRealTime={validateAgeStartedGamingRealTime}
        validateHoursPerDay={validateHoursPerDay}
        campaign={campaign}
      />

      {/* Client Contact Method Section */}
      <ContactMethod
        enrollmentData={enrollmentData}
        updateEnrollmentData={updateEnrollmentData}
        generateQRCode={generateQRCode}
        sendEmailLink={sendEmailLink}
        sessionId={sessionId}
      />

      {/* Opt-In Form Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-blue-900">Opt-In Form Submission</h3>
            <p className="text-xs text-blue-700">Submit to vendor opt-in form (atslawsuits.com)</p>
          </div>
        </div>

        {/* Status Display */}
        {enrollmentData.optInSubmissionStatus && (
          <div className={`mb-4 p-3 rounded-lg border ${
            enrollmentData.optInSubmissionStatus === 'success' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center space-x-2">
              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                enrollmentData.optInSubmissionStatus === 'success' 
                  ? 'bg-green-500' 
                  : 'bg-red-500'
              }`}>
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {enrollmentData.optInSubmissionStatus === 'success' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  )}
                </svg>
              </div>
              <span className={`text-sm font-medium ${
                enrollmentData.optInSubmissionStatus === 'success' 
                  ? 'text-green-800' 
                  : 'text-red-800'
              }`}>
                Status: {enrollmentData.optInSubmissionStatus === 'success' ? 'PASS' : 'FAIL'}
              </span>
              {enrollmentData.optInSubmittedAt && (
                <span className="text-xs text-gray-600">
                  ({new Date(enrollmentData.optInSubmittedAt).toLocaleString()})
                </span>
              )}
            </div>
          </div>
        )}

        {optInStatus === 'idle' && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">Ready to submit to opt-in form</span>
            <button
              onClick={handleOptInSubmission}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!enrollmentData.intakeFirstName || !enrollmentData.intakeLastName}
            >
              Submit to Opt-In Form
            </button>
          </div>
        )}

        {optInStatus === 'submitting' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">{optInMessage}</span>
            </div>
            <button
              onClick={handleOptInSubmission}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors opacity-50 cursor-not-allowed"
              disabled={true}
            >
              Submit to Opt-In Form
            </button>
          </div>
        )}

        {optInStatus === 'success' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm text-green-800 font-medium">{optInMessage}</span>
            </div>
            <button
              onClick={handleOptInSubmission}
              className={`px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors ${
                enrollmentData.optInSubmissionStatus === 'success' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={enrollmentData.optInSubmissionStatus === 'success'}
            >
              Submit to Opt-In Form
            </button>
          </div>
        )}

        {optInStatus === 'error' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <span className="text-sm text-red-800 font-medium">{optInMessage}</span>
            </div>
            <button
              onClick={handleOptInSubmission}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
        <button className="btn-outline flex items-center justify-center space-x-2 w-full sm:w-auto" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        {enrollmentData.clientContactMethod && (
          enrollmentData.clientContactMethod === 'email'
            ? (
              (enrollmentData.emailLinkVerified || enrollmentData.identityConsentAgreed) && (
                <button 
                  className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleContinue}
                  disabled={isNavigating || enrollmentData.optInSubmissionStatus !== 'success'}
                >
                  {isNavigating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4" />
                  )}
                  <span>{isNavigating ? 'Saving...' : 'Continue'}</span>
                </button>
              )
            )
            : (
              <button 
                className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleContinue}
                disabled={isNavigating || enrollmentData.optInSubmissionStatus !== 'success'}
              >
                {isNavigating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                <span>{isNavigating ? 'Saving...' : 'Continue'}</span>
              </button>
            )
        )}
      </div>
    </div>
  )
}